import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Card } from "@/components/PortalPage";

const LABEL = {
  quote_created: "Quote created", invoice_created: "Invoice created", quote_modified: "Quote edited", invoice_modified: "Invoice edited",
  customer_reassigned: "Customer changed", status_changed: "Status changed", voided: "Voided", internally_converted: "Converted internally (not customer acceptance)",
  payment_recorded: "Payment recorded", email_sent: "Emailed", customer_viewed: "Customer viewed", customer_signed: "Customer signed & accepted",
  admin_override: "ALR admin override", test_flag_changed: "Test flag changed", job_created: "Job created", job_modified: "Job edited",
  job_status_changed: "Job status changed", job_scheduled: "Job scheduled", import_performed: "Imported",
  reopened_for_reapproval: "Reopened for customer re-approval",
};
const when = (s) => new Date(s).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export const RecordTools = ({ kind, rec, onChange }) => {
  const [rows, setRows] = useState(null);
  const base = kind === "job" ? `/billing/jobs/${rec.id}` : `/billing/docs/${rec.id}`;
  const toggle = async () => {
    if (!rec.is_test && !window.confirm("Mark this as a TEST record? It will be left out of revenue, receivables, royalties, reports and reminders. Nothing is deleted.")) return;
    try { onChange(await api(`${base}/test-flag`, { method: "POST", body: { is_test: !rec.is_test } })); toast.success(rec.is_test ? "Back to a normal record" : "Marked as a test record"); } catch (e) { toast.error(e.message); }
  };
  const load = async () => { try { setRows(await api(`${base}/history`)); } catch (e) { toast.error(e.message); } };
  const acc = rec.acceptance || {};
  return (
    <Card className="mt-4" data-testid="record-tools">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex-1 text-xs font-extrabold tracking-[1.5px] text-neutral-400">RECORD STATUS & HISTORY</p>
        {rec.is_test && <span className="rounded-full bg-[#F59E0B] px-2.5 py-0.5 text-[10px] font-extrabold text-black" data-testid="record-test-badge">TEST RECORD</span>}
        <button onClick={toggle} className="rounded-full border border-[#555] px-3 py-1 text-[11px] font-bold" data-testid="record-test-toggle">{rec.is_test ? "Unmark test record" : "Mark as test record"}</button>
        <button onClick={load} className="rounded-full border border-[#555] px-3 py-1 text-[11px] font-bold" data-testid="record-history-btn">Show change history</button>
      </div>
      {kind === "quote" && (
        <div className="mt-2 grid gap-1 text-[11px] text-neutral-400 sm:grid-cols-2" data-testid="record-acceptance">
          <p>Customer signed: <b className="text-white">{rec.approved_by ? `${rec.approved_by} · ${when(rec.approved_at || acc.customer_signed_at)}` : "No"}</b></p>
          <p>Converted internally: <b className="text-white">{acc.internal_converted_at ? when(acc.internal_converted_at) : rec.status === "converted" && !rec.approved_by ? "Yes (date not recorded)" : "No"}</b></p>
          {acc.override_at && <p className="sm:col-span-2">ALR override: <b className="text-white">{when(acc.override_at)} · {acc.override_reason}</b></p>}
        </div>
      )}
      {rows && (
        <div className="mt-3" data-testid="record-email-log">
          {(rec.email_log || []).map((e, i) => (
            <p key={i} className="mb-1 rounded-lg bg-[#121212] p-2 text-[11px]" data-testid={`record-email-${i}`}><b>Emailed {when(e.at)}</b> to {e.to}{e.card_offered === false && rec.kind === "invoice" ? " · card payment not offered" : ""}
              {e.message ? <span className="mt-1 block whitespace-pre-wrap text-neutral-300">“{e.message}”</span> : <span className="text-neutral-500"> · no personal message</span>}</p>
          ))}
        </div>
      )}
      {rows && (
        <ol className="mt-3 space-y-1.5" data-testid="record-history">
          {!rows.length && <li className="text-[11px] text-neutral-500">No changes recorded yet. History starts from Oct 27, 2026.</li>}
          {rows.map((r) => (
            <li key={r.id} className="rounded-lg bg-[#121212] p-2 text-[11px]" data-testid={`record-history-${r.id}`}>
              <b>{LABEL[r.action] || r.action}</b> · {when(r.at)} · {r.actor}{r.impersonated_by ? " (ALR support)" : ""}
              {r.note && <span className="text-neutral-400"> · {r.note}</span>}
              {!r.action.endsWith("_created") && Object.keys(r.changes || {}).length > 0 && <span className="block text-neutral-500">Changed: {Object.keys(r.changes).join(", ")}</span>}
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
};
